/**
 * Theme configuration for the Lecture Platform
 * Contains colors, sizes, breakpoints, and design elements for consistent styling
 */

// Helper to safely get nested properties
const get = (obj, path, defaultValue = undefined) => {
  const keys = path.split('.');
  let result = obj;
  for (const key of keys) {
    result = result?.[key];
    if (result === undefined) {
      return defaultValue;
    }
  }
  return result;
};

// Relative luminance (WCAG 2.0) — returns 0 (black) to 1 (white)
const getLuminance = (hex) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

// WCAG contrast ratio between two hex colors
const getContrastRatio = (hex1, hex2) => {
  const l1 = getLuminance(hex1);
  const l2 = getLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

// Darken a hex color by a factor (0 = unchanged, 1 = black)
const darkenHex = (hex, factor) => {
  const h = hex.replace('#', '');
  const r = Math.round(parseInt(h.substring(0, 2), 16) * (1 - factor));
  const g = Math.round(parseInt(h.substring(2, 4), 16) * (1 - factor));
  const b = Math.round(parseInt(h.substring(4, 6), 16) * (1 - factor));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Ensure a color meets minimum contrast against a background.
// Progressively darkens (on light bg) or lightens logic handled by caller.
// Returns the original color if it already passes, else a darkened version.
const ensureContrast = (fgHex, bgHex, minRatio = 3.0) => {
  if (getContrastRatio(fgHex, bgHex) >= minRatio) return fgHex;
  // Progressively darken until contrast is met (max 10 steps to avoid infinite loop)
  let adjusted = fgHex;
  for (let i = 1; i <= 10; i++) {
    adjusted = darkenHex(fgHex, i * 0.08); // 8% darker each step
    if (getContrastRatio(adjusted, bgHex) >= minRatio) return adjusted;
  }
  return adjusted; // Best effort
};

// Define default colors and other theme properties
const defaultTheme = {
  colors: {
    primary: '#1e4273',     // Default Deep blue
    secondary: '#f5f7fa',    // Default Light blue-gray
    accent: '#e67e22',      // Default Orange
    text: '#333333',        // Default Dark gray
    darkGray: '#666666',
    mediumGray: '#999999',
    lightGray: '#f0f2f5',
    background: '#f8f9fa',   // Default Off-white
    success: '#27ae60',
    error: '#e74c3c',
    warning: '#f39c12'
  },
  fonts: {
    main: "'Roboto', 'Segoe UI', 'Helvetica Neue', sans-serif",
    heading: "'Poppins', 'Segoe UI', 'Helvetica Neue', sans-serif"
  },
  fontSizes: {
    xs: '12px',
    sm: '14px',
    md: '16px',
    lg: '18px',
    xl: '24px',
    xxl: '32px'
  },
  borderRadius: {
    small: '4px',
    medium: '8px',
    large: '12px',
    circle: '50%'
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px'
  },
  boxShadow: {
    small: '0 2px 4px rgba(0, 0, 0, 0.1)',
    medium: '0 4px 8px rgba(0, 0, 0, 0.1)',
    large: '0 8px 16px rgba(0, 0, 0, 0.1)',
    card: '0 4px 16px rgba(0, 0, 0, 0.08)',
    elevated: '0 8px 24px rgba(0, 0, 0, 0.12)'
  },
  gradients: {
    primary: 'linear-gradient(135deg, #1e4273 0%, #2c5a9e 100%)', // Example gradient
    accent: 'linear-gradient(135deg, #e67e22 0%, #f39c12 100%)', // Example gradient
    card: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(0,0,0,0.05) 100%)'
  },
  transitions: {
    fast: '0.2s ease',
    medium: '0.3s ease',
    slow: '0.5s ease'
  },
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    largeDesktop: '1200px'
  },
  container: {
    maxWidth: '1200px',
    padding: '0 16px'
  }
};

/**
 * Creates a theme object by merging configuration colors with defaults.
 * @param {object} config - The loaded application configuration.
 * @returns {object} The final theme object.
 */
const createTheme = (config = {}) => {
  // Get colors from config, fallback to empty object if not present
  const configColors = get(config, 'colors', {});

  // Merge default colors with config colors (config takes precedence)
  const mergedColors = {
    ...defaultTheme.colors,
    ...configColors,
  };

  // Detect dark content background.
  const bgLum = getLuminance(mergedColors.background);
  const isDarkBg = bgLum < 0.15;

  // Map config's "foreground" to theme's "text" — but ensure it has contrast
  // with the actual background. Prevents light-text-on-light-bg when background
  // changes from dark to light while foreground stays light.
  if (configColors.foreground && mergedColors.text === defaultTheme.colors.text) {
    const fgContrast = getContrastRatio(configColors.foreground, mergedColors.background);
    if (fgContrast >= 4.5) {
      // Foreground has good contrast with background — use it
      mergedColors.text = configColors.foreground;
    }
    // Otherwise keep the default dark text — the foreground color doesn't
    // work on this background (e.g. light foreground on light background)
  }

  // Card text colors — always dark because cards always have white/light backgrounds
  mergedColors.cardText = '#333333';
  mergedColors.cardTextSecondary = '#64748b';

  // Card info box background — derived from the center's background color
  // so warm themes get warm greys, cool themes get cool greys automatically
  mergedColors.cardInfoBg = darkenHex(mergedColors.background, 0.05);
  mergedColors.cardInfoBgEnd = darkenHex(mergedColors.background, 0.10);

  // --- Contrast-aware heading color selection ---
  // Principle: use the center's secondary color (e.g. gold) for headings whenever
  // it has sufficient contrast with the background. If not, darken it until it does.
  // This preserves the center's brand identity across both dark and light themes.
  const hasSecondary = configColors.secondary && configColors.secondary !== defaultTheme.colors.secondary;

  if (isDarkBg) {
    // Dark background — secondary usually has plenty of contrast
    mergedColors.headingColor = hasSecondary
      ? ensureContrast(mergedColors.secondary, mergedColors.background, 3.0)
      : mergedColors.secondary;
    mergedColors.subtitleColor = 'rgba(255,255,255,0.55)';
    mergedColors.linkColor = mergedColors.accent;
    mergedColors.darkGray = 'rgba(255,255,255,0.7)';
    mergedColors.mediumGray = 'rgba(255,255,255,0.5)';
    mergedColors.lightGray = 'rgba(255,255,255,0.08)';
    mergedColors.cardBg = 'rgba(255,255,255,0.06)';
  } else {
    // Light background — use secondary for headings if it has contrast,
    // otherwise fall back to primary (which is typically dark enough)
    if (hasSecondary) {
      mergedColors.headingColor = ensureContrast(mergedColors.secondary, mergedColors.background, 3.0);
    } else {
      mergedColors.headingColor = mergedColors.primary;
    }
    mergedColors.subtitleColor = mergedColors.darkGray;
    mergedColors.linkColor = ensureContrast(mergedColors.primary, mergedColors.background, 3.0);
    mergedColors.cardBg = '#ffffff';
  }

  // Return the full theme object with merged colors
  return {
    ...defaultTheme, // Spread all default properties first
    colors: mergedColors, // Override with merged colors
    isDarkBackground: isDarkBg,
  };
};

export default createTheme;
